"use client";

import { c as _c } from "react/compiler-runtime";
import { coerceMultipleChoiceState, formatMultipleChoiceAnswer } from "@bg-bgi/magnus-sdk";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import { CheckIcon, SendHorizontalIcon } from "lucide-react";
import { type ChangeEvent, type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ActiveMultipleChoicePrompt } from "./multiple-choice-prompt";
type Answer = {
  selected: readonly number[];
  otherSelected: boolean;
  otherText: string;
};
const EMPTY_ANSWER: Answer = {
  selected: [],
  otherSelected: false,
  otherText: ""
};

/**
 * Input-mode picker for the `multipleChoice` tool. Rendered by the composer
 * in place of the standard input while the latest assistant turn is
 * waiting on a choice (see `getActiveMultipleChoice`). The user picks
 * option(s) — and/or types a free-text answer when `allowOther` is set —
 * then hits Submit, which sends their selection verbatim as a new user
 * message. "Skip" sends `MULTIPLE_CHOICE_SKIP_MESSAGE`, which tells the
 * model the user declined and collapses the picker back to the standard
 * input.
 *
 * The model can call `multipleChoice` more than once in a single turn, so
 * this takes a list: every pending question is stacked in call order and
 * answered in one pass, because Submit sends one user message and that
 * message ends the turn. Answering only some of them is allowed — the
 * combined message carries just the ones the user picked for.
 *
 * Selection lives in local state keyed by tool call id; the component is
 * keyed by the batch's `key` upstream so it always mounts fresh.
 */
export function MultipleChoiceInput(t0) {
  const $ = _c(70);
  const {
    prompts,
    onSubmit,
    onSkip,
    disabled: t1,
    submitDisabled: t2
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const submitDisabled = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [answers, setAnswers] = useState(t3);
  let t4;
  if ($[1] !== prompts) {
    t4 = prompts.map(_temp).filter(_temp2);
    $[1] = prompts;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const questions = t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (toolCallId_0, next) => {
      setAnswers(prev => ({
        ...prev,
        [toolCallId_0]: next(prev[toolCallId_0] ?? EMPTY_ANSWER)
      }));
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const updateAnswer = t5;
  let t6;
  if ($[4] !== disabled) {
    t6 = (toolCallId_1, multiple, index_0) => {
      if (disabled) {
        return;
      }
      updateAnswer(toolCallId_1, prev_0 => {
        if (multiple) {
          return {
            ...prev_0,
            selected: prev_0.selected.includes(index_0) ? prev_0.selected.filter(i => i !== index_0) : [...prev_0.selected, index_0]
          };
        }
        return {
          ...prev_0,
          selected: [index_0],
          otherSelected: false
        };
      });
    };
    $[4] = disabled;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const toggleOption = t6;
  let t7;
  if ($[6] !== disabled) {
    t7 = (toolCallId_2, multiple_0) => {
      if (disabled) {
        return;
      }
      updateAnswer(toolCallId_2, prev_1 => ({
        ...prev_1,
        otherSelected: true,
        selected: multiple_0 ? prev_1.selected : []
      }));
    };
    $[6] = disabled;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const selectOther = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = toolCallId_3 => {
      updateAnswer(toolCallId_3, _temp3);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const deselectOther = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (toolCallId_4, multiple_1, text) => {
      updateAnswer(toolCallId_4, prev_3 => ({
        ...prev_3,
        otherText: text,
        otherSelected: true,
        selected: multiple_1 ? prev_3.selected : []
      }));
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const setOtherText = t9;
  let t10;
  if ($[10] !== answers || $[11] !== questions) {
    let t11;
    if ($[13] !== answers) {
      t11 = q_0 => {
        const answer = answers[q_0.toolCallId] ?? EMPTY_ANSWER;
        const trimmedOther = answer.otherText.trim();
        const parts = [...answer.selected].sort(_temp4).map(i_0 => q_0.options[i_0]).filter(_temp5);
        if (answer.otherSelected && trimmedOther.length > 0) {
          parts.push(trimmedOther);
        }
        return {
          ...q_0,
          answer,
          parts,
          otherInvalid: answer.otherSelected && trimmedOther.length === 0
        };
      };
      $[13] = answers;
      $[14] = t11;
    } else {
      t11 = $[14];
    }
    t10 = questions.map(t11);
    $[10] = answers;
    $[11] = questions;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const resolved = t10;
  let t11;
  if ($[15] !== resolved) {
    t11 = resolved.filter(_temp6);
    $[15] = resolved;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const answeredCount = t11.length;
  let t12;
  if ($[17] !== answeredCount || $[18] !== disabled || $[19] !== resolved || $[20] !== submitDisabled) {
    t12 = !disabled && !submitDisabled && !resolved.some(_temp7) && answeredCount > 0;
    $[17] = answeredCount;
    $[18] = disabled;
    $[19] = resolved;
    $[20] = submitDisabled;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const canSubmit = t12;
  const actionsDisabled = disabled || submitDisabled;
  let t13;
  if ($[22] !== canSubmit || $[23] !== onSubmit || $[24] !== resolved) {
    t13 = () => {
      if (!canSubmit) {
        return;
      }
      onSubmit(formatMultipleChoiceAnswer(resolved.map(_temp8)));
    };
    $[22] = canSubmit;
    $[23] = onSubmit;
    $[24] = resolved;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleSubmit = t13;
  const containerRef = useRef(null);
  let t14;
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      containerRef.current?.focus();
    };
    t15 = [];
    $[26] = t14;
    $[27] = t15;
  } else {
    t14 = $[26];
    t15 = $[27];
  }
  useEffect(t14, t15);
  let t16;
  if ($[28] !== canSubmit || $[29] !== handleSubmit) {
    t16 = e => {
      if (e.key !== "Enter" || e.shiftKey || !canSubmit) {
        return;
      }
      if ((e.target as HTMLElement).tagName === "TEXTAREA") {
        return;
      }
      e.preventDefault();
      handleSubmit();
    };
    $[28] = canSubmit;
    $[29] = handleSubmit;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const handleContainerKeyDown = t16;
  if (resolved.length === 0) {
    return null;
  }
  const isBatch = resolved.length > 1;
  const t17 = isBatch && "max-h-[50vh] overflow-y-auto";
  let t18;
  if ($[31] !== t17) {
    t18 = cn(t17);
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== canSubmit || $[34] !== disabled || $[35] !== handleSubmit || $[36] !== isBatch || $[37] !== resolved || $[38] !== selectOther || $[39] !== toggleOption) {
    let t20;
    if ($[41] !== canSubmit || $[42] !== disabled || $[43] !== handleSubmit || $[44] !== isBatch || $[45] !== selectOther || $[46] !== toggleOption) {
      t20 = (q_3, questionIndex) => <div className={cn(questionIndex > 0 && "border-t border-border/60")} key={q_3.toolCallId}>{q_3.question && <p className="px-4 pt-3.5 pb-2 text-sm font-medium text-foreground">{q_3.question}</p>}<div className="flex flex-col gap-1.5 px-3 pb-1">{q_3.items.map(item => <OptionRow autoFocus={questionIndex === 0 && item.index === 0} canSubmit={canSubmit && !isBatch} disabled={disabled} isSelected={q_3.answer.selected.includes(item.index)} key={item.id} label={item.label} multiple={q_3.multiple} onSelect={() => toggleOption(q_3.toolCallId, q_3.multiple, item.index)} onSubmit={handleSubmit} />)}{q_3.allowOther && <OtherOptionRow disabled={disabled} invalid={q_3.otherInvalid} isSelected={q_3.answer.otherSelected} multiple={q_3.multiple} onChange={e_0 => setOtherText(q_3.toolCallId, q_3.multiple, e_0.target.value)} onDeselect={() => deselectOther(q_3.toolCallId)} onKeyDown={e_1 => {
            if (e_1.key === "Enter" && !e_1.shiftKey) {
              e_1.preventDefault();
              e_1.stopPropagation();
              if (isBatch) {
                e_1.currentTarget.blur();
                return;
              }
              handleSubmit();
            }
          }} onSelect={() => selectOther(q_3.toolCallId, q_3.multiple)} value={q_3.answer.otherText} />}</div></div>;
      $[41] = canSubmit;
      $[42] = disabled;
      $[43] = handleSubmit;
      $[44] = isBatch;
      $[45] = selectOther;
      $[46] = toggleOption;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    t19 = resolved.map(t20);
    $[33] = canSubmit;
    $[34] = disabled;
    $[35] = handleSubmit;
    $[36] = isBatch;
    $[37] = resolved;
    $[38] = selectOther;
    $[39] = toggleOption;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[48] !== t18 || $[49] !== t19) {
    t20 = <div className={t18}>{t19}</div>;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== actionsDisabled || $[52] !== onSkip) {
    t21 = <Button className="text-muted-foreground" disabled={actionsDisabled} onClick={onSkip} size="sm" type="button" variant="ghost">Skip</Button>;
    $[51] = actionsDisabled;
    $[52] = onSkip;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  const t22 = isBatch ? `${answeredCount} of ${resolved.length} answered` : resolved[0]?.multiple ? "Select one or more" : "Select one";
  let t23;
  if ($[54] !== t22) {
    t23 = <span className="hidden truncate text-xs text-muted-foreground sm:inline">{t22}</span>;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  const t24 = !canSubmit;
  let t25;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <SendHorizontalIcon className="size-3.5" />;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== handleSubmit || $[58] !== t24) {
    t26 = <Button aria-label="Submit selection" disabled={t24} onClick={handleSubmit} size="sm" type="button">{t25}Submit</Button>;
    $[57] = handleSubmit;
    $[58] = t24;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t23 || $[61] !== t26) {
    t27 = <div className="flex items-center gap-2">{t23}{t26}</div>;
    $[60] = t23;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t21 || $[64] !== t27) {
    t28 = <div className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2">{t21}{t27}</div>;
    $[63] = t21;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== handleContainerKeyDown || $[67] !== t20 || $[68] !== t28) {
    t29 = <div className="w-full rounded-2xl border bg-composer-panel outline-none" onKeyDown={handleContainerKeyDown} ref={containerRef} tabIndex={-1}>{t20}{t28}</div>;
    $[66] = handleContainerKeyDown;
    $[67] = t20;
    $[68] = t28;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  return t29;
}
function _temp8(t0) {
  const {
    question,
    multiple: multiple_2,
    parts: parts_0
  } = t0;
  return {
    question,
    multiple: multiple_2,
    parts: parts_0
  };
}
function _temp7(q_2) {
  return q_2.otherInvalid;
}
function _temp6(q_1) {
  return q_1.parts.length > 0;
}
function _temp5(label_0) {
  return Boolean(label_0);
}
function _temp4(a, b) {
  return a - b;
}
function _temp3(prev_2) {
  return {
    ...prev_2,
    otherSelected: false
  };
}
function _temp2(q) {
  return q.options.length > 0;
}
function _temp(t0) {
  const {
    toolCallId,
    prompt
  } = t0;
  const options = coerceMultipleChoiceState(prompt, {
    requireOptions: false
  })?.options ?? [];
  return {
    toolCallId,
    question: prompt.question,
    multiple: prompt.multiple,
    allowOther: prompt.allowOther,
    options,
    items: options.map((label, index) => ({
      id: `${toolCallId}-opt-${index}`,
      label,
      index
    }))
  };
}
function OptionRow(t0) {
  const $ = _c(27);
  const {
    label,
    isSelected,
    disabled,
    multiple,
    canSubmit,
    onSelect,
    onSubmit,
    autoFocus: t1
  } = t0;
  const autoFocus = t1 === undefined ? false : t1;
  const t2 = isSelected ? "border-primary bg-primary/5 text-foreground" : "border-input text-foreground hover:bg-muted/50";
  const t3 = disabled ? "cursor-default opacity-70" : "cursor-pointer";
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = cn("flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors", "outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50", t2, t3);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== canSubmit || $[4] !== multiple || $[5] !== onSelect || $[6] !== onSubmit) {
    t5 = e => {
      if (e.key !== "Enter") {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (canSubmit) {
        onSubmit();
        return;
      }
      if (!multiple) {
        onSelect();
        return;
      }
      onSelect();
    };
    $[3] = canSubmit;
    $[4] = multiple;
    $[5] = onSelect;
    $[6] = onSubmit;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = multiple ? "rounded-[4px]" : "rounded-full";
  const t7 = isSelected ? "border-primary bg-primary text-primary-foreground" : "border-input";
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = cn("flex size-4 shrink-0 items-center justify-center border transition-colors", t6, t7);
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isSelected) {
    t9 = isSelected && <CheckIcon className="size-3" />;
    $[11] = isSelected;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8 || $[14] !== t9) {
    t10 = <span className={t8}>{t9}</span>;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== label) {
    t11 = <span className="min-w-0 flex-1 break-words">{label}</span>;
    $[16] = label;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== autoFocus || $[19] !== disabled || $[20] !== isSelected || $[21] !== onSelect || $[22] !== t10 || $[23] !== t11 || $[24] !== t4 || $[25] !== t5) {
    t12 = <button aria-pressed={isSelected} autoFocus={autoFocus} className={t4} disabled={disabled} onClick={onSelect} onKeyDown={t5} type="button">{t10}{t11}</button>;
    $[18] = autoFocus;
    $[19] = disabled;
    $[20] = isSelected;
    $[21] = onSelect;
    $[22] = t10;
    $[23] = t11;
    $[24] = t4;
    $[25] = t5;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}

/**
 * The "Other" option with its free-text field embedded inside the row.
 * Clicking anywhere in the row (it's a `<label>`) focuses the textarea,
 * and focusing it auto-selects the option — so typing always counts as
 * choosing "Other". The textarea auto-grows with its content (via CSS
 * `field-sizing`) and wraps long answers. The indicator button
 * additionally toggles the option off in multi-select mode.
 */
function OtherOptionRow(t0) {
  const $ = _c(31);
  const {
    isSelected,
    disabled,
    multiple,
    value,
    invalid,
    onSelect,
    onDeselect,
    onChange,
    onKeyDown
  } = t0;
  const inputRef = useRef(null);
  const t1 = isSelected ? "border-primary bg-primary/5 text-foreground" : "border-input text-foreground hover:bg-muted/50";
  const t2 = disabled ? "cursor-default opacity-70" : "cursor-text";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cn("flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors", "focus-within:ring-[3px] focus-within:ring-ring/50", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = isSelected ? "Deselect Other" : "Select Other";
  const t5 = multiple ? "rounded-[4px]" : "rounded-full";
  const t6 = isSelected ? "border-primary bg-primary text-primary-foreground" : "border-input";
  let t7;
  if ($[3] !== t5 || $[4] !== t6) {
    t7 = cn("mt-0.5 flex size-4 shrink-0 items-center justify-center border transition-colors", t5, t6);
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== isSelected || $[7] !== multiple || $[8] !== onDeselect || $[9] !== onSelect) {
    t8 = e => {
      e.preventDefault();
      if (isSelected && multiple) {
        onDeselect();
        return;
      }
      onSelect();
      inputRef.current?.focus();
    };
    $[6] = isSelected;
    $[7] = multiple;
    $[8] = onDeselect;
    $[9] = onSelect;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isSelected) {
    t9 = isSelected && <CheckIcon className="size-3" />;
    $[11] = isSelected;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== disabled || $[14] !== isSelected || $[15] !== t4 || $[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t10 = <button aria-label={t4} aria-pressed={isSelected} className={t7} disabled={disabled} onClick={t8} type="button">{t9}</button>;
    $[13] = disabled;
    $[14] = isSelected;
    $[15] = t4;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== disabled || $[21] !== invalid || $[22] !== onChange || $[23] !== onKeyDown || $[24] !== onSelect || $[25] !== value) {
    t11 = <textarea aria-invalid={invalid} aria-label="Custom answer" className="field-sizing-content min-w-0 flex-1 resize-none bg-transparent text-foreground outline-none placeholder:text-muted-foreground" disabled={disabled} onChange={onChange} onFocus={onSelect} onKeyDown={onKeyDown} placeholder={"Other\u2026"} ref={inputRef} rows={1} value={value} />;
    $[20] = disabled;
    $[21] = invalid;
    $[22] = onChange;
    $[23] = onKeyDown;
    $[24] = onSelect;
    $[25] = value;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t3) {
    t12 = <label className={t3}>{t10}{t11}</label>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t3;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}